import React, { useState } from 'react';
import { Box, Button, Text, VStack, HStack, Badge, Textarea, useToast, Card, CardHeader, CardBody, Grid, Flex } from '@chakra-ui/react';
import { callApi } from '../../callApi';

const JudgementCallSection = ({ reportData, accessToken, onSaveSuccess }) => {
  const toast = useToast();
  const [savingCpt, setSavingCpt] = useState(false);
  const [savingIcd10, setSavingIcd10] = useState(false);

  const cptJudgement = reportData?.cpt_ai_evaluation?.judgement_call;
  const icd10Judgement = reportData?.icd10_ai_evaluation?.judgement_call;

  const cptSeverity = reportData?.cpt_ai_evaluation?.variance_severity;
  const isCptSuppressed = cptSeverity && cptSeverity.toLowerCase() === 'minor';
  const hasCptQuestion = cptJudgement?.needs_review && cptJudgement?.review_score >= 8 && cptJudgement?.questions?.length > 0 && !isCptSuppressed;
  
  const revedyScore = reportData?.icd10_ai_evaluation?.revedy_score || 0;
  const claimScore = reportData?.icd10_ai_evaluation?.claim_score || 0;
  const isIcd10Suppressed = (claimScore - revedyScore) > 1;
  
  const hasIcd10Question = icd10Judgement?.needs_review && icd10Judgement?.review_score >= 8 && icd10Judgement?.questions?.length > 0 && !isIcd10Suppressed;

  const [cptAnswer, setCptAnswer] = useState(cptJudgement?.reviewer_answer || '');
  const [icd10Answer, setIcd10Answer] = useState(icd10Judgement?.reviewer_answer || '');

  if (!hasCptQuestion && !hasIcd10Question) return null;

  const handleSave = async (type, answer, setSaving) => {
    setSaving(true);
    try {
      const response = await callApi(`/api/mph/coding-variance/${reportData.internal_case_id}/judgement-answer`, accessToken, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ type, answer })
      });
      const result = await response.json();
      if (result.status === 'success') {
        toast({
          title: "Answer Saved",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        if (onSaveSuccess) {
          onSaveSuccess();
        }
      } else {
        throw new Error(result.message || 'Failed to save answer');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <VStack spacing={4} align="stretch" mb={6}>
      {hasCptQuestion && (
        <Card shadow="sm" borderWidth="1px" borderColor="purple.200">
          <CardHeader bg="purple.50" py={3} borderBottomWidth="1px">
            <HStack>
              <Badge colorScheme="purple">CPT Policy Question</Badge>
              <Text fontSize="sm" color="gray.600">Score: {cptJudgement.review_score}/10</Text>
            </HStack>
          </CardHeader>
          <CardBody>
            <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={6}>
              <VStack align="stretch" spacing={4}>
                {cptJudgement.questions.map((q, idx) => (
                  <Box key={idx} p={3} bg="white" rounded="md" borderWidth="1px" borderColor="gray.200">
                    <Text fontWeight="bold" fontSize="sm" color="gray.700" mb={1}>Question:</Text>
                    <Text fontSize="sm">{q}</Text>
                  </Box>
                ))}
              </VStack>
              <Flex direction="column" h="100%">
                <Text fontWeight="bold" fontSize="sm" color="gray.700" mb={2}>Your Answer / Policy Decision:</Text>
                <Textarea 
                  value={cptAnswer}
                  onChange={(e) => setCptAnswer(e.target.value)}
                  placeholder="Enter your decision here..."
                  size="sm"
                  flex="1"
                  minH="100px"
                  bg="white"
                  mb={3}
                />
                <Flex justify="space-between" align="center">
                  <Box>
                    {cptJudgement.answer_history?.length > 0 && (
                      <Text fontSize="xs" color="gray.500" fontStyle="italic">
                        Edited {cptJudgement.answer_history.length} time(s)
                      </Text>
                    )}
                  </Box>
                  <Button 
                    colorScheme="purple" 
                    size="sm" 
                    isLoading={savingCpt}
                    onClick={() => handleSave('cpt', cptAnswer, setSavingCpt)}
                  >
                    Save CPT Answer
                  </Button>
                </Flex>
              </Flex>
            </Grid>
          </CardBody>
        </Card>
      )}

      {hasIcd10Question && (
        <Card shadow="sm" borderWidth="1px" borderColor="purple.200">
          <CardHeader bg="purple.50" py={3} borderBottomWidth="1px">
            <HStack>
              <Badge colorScheme="purple">ICD-10 Policy Question</Badge>
              <Text fontSize="sm" color="gray.600">Score: {icd10Judgement.review_score}/10</Text>
            </HStack>
          </CardHeader>
          <CardBody>
            <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={6}>
              <VStack align="stretch" spacing={4}>
                {icd10Judgement.questions.map((q, idx) => (
                  <Box key={idx} p={3} bg="white" rounded="md" borderWidth="1px" borderColor="gray.200">
                    <Text fontWeight="bold" fontSize="sm" color="gray.700" mb={1}>Question:</Text>
                    <Text fontSize="sm">{q}</Text>
                  </Box>
                ))}
              </VStack>
              <Flex direction="column" h="100%">
                <Text fontWeight="bold" fontSize="sm" color="gray.700" mb={2}>Your Answer / Policy Decision:</Text>
                <Textarea 
                  value={icd10Answer}
                  onChange={(e) => setIcd10Answer(e.target.value)}
                  placeholder="Enter your decision here..."
                  size="sm"
                  flex="1"
                  minH="100px"
                  bg="white"
                  mb={3}
                />
                <Flex justify="space-between" align="center">
                  <Box>
                    {icd10Judgement.answer_history?.length > 0 && (
                      <Text fontSize="xs" color="gray.500" fontStyle="italic">
                        Edited {icd10Judgement.answer_history.length} time(s)
                      </Text>
                    )}
                  </Box>
                  <Button 
                    colorScheme="purple" 
                    size="sm" 
                    isLoading={savingIcd10}
                    onClick={() => handleSave('icd10', icd10Answer, setSavingIcd10)}
                  >
                    Save ICD-10 Answer
                  </Button>
                </Flex>
              </Flex>
            </Grid>
          </CardBody>
        </Card>
      )}
    </VStack>
  );
};

export default JudgementCallSection;
